/**
 * Normalizes different date formats to Unix timestamp (milliseconds)
 * Handles Date objects, timestamp strings, ISO 8601 date strings, and numbers.
 *
 * @param date - Date in various formats (Date, timestamp string, ISO 8601 string, or number)
 * @returns Unix timestamp in milliseconds, or 0 if invalid
 */
export const normalizeToTimestamp = (
  date: Date | string | number | null | undefined,
): number => {
  if (date == null) return 0;

  if (date instanceof Date) {
    const ts = date.getTime();
    return isNaN(ts) ? 0 : ts;
  }

  if (typeof date === 'number') {
    // Check if the number needs conversion from seconds to milliseconds:
    // 1. Has decimal part (e.g., 1773158195.678 - seconds with fractional milliseconds)
    // 2. Is a small integer < 10 billion (e.g., 1773158195 - seconds as integer)
    //
    // Why this works:
    // - Timestamps in milliseconds are always >= 10^12 for dates after year 2001
    // - The decimal check catches: 1773158195.678 (OpenSearch format)
    // - The magnitude check catches: 1773158195 (seconds as integer)
    // - Both checks together handle all seconds-based timestamps
    if (date % 1 !== 0 || date < 10000000000) {
      // Convert seconds to milliseconds
      return Math.round(date * 1000);
    }
    // Already in milliseconds (e.g., 1773158195678 - DDB format)
    return date;
  }

  if (typeof date === 'string') {
    // Check if it's already a timestamp string (all digits)
    if (/^\d+$/.test(date)) {
      return parseInt(date, 10);
    }

    // Otherwise it's ISO 8601 format
    const timestamp = new Date(date).getTime();
    return isNaN(timestamp) ? 0 : timestamp;
  }

  return 0;
};

/**
 * Compares two dates that may be in different formats
 * Returns true if they represent the same moment in time
 *
 * @param date1 - First date (timestamp string, ISO 8601 string, or number)
 * @param date2 - Second date (timestamp string, ISO 8601 string, or number)
 * @returns true if dates are equal, false otherwise
 */
export const areDatesEqual = (
  date1: Date | string | number | null | undefined,
  date2: Date | string | number | null | undefined,
): boolean => {
  const timestamp1 = normalizeToTimestamp(date1);
  const timestamp2 = normalizeToTimestamp(date2);

  return timestamp1 === timestamp2;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Returns the number of full days from now until the given date.
 * Positive = future, zero or negative = past/today.
 * Returns Infinity if the date is null/undefined/invalid.
 *
 * @param date - Target date in any supported format
 */
export const daysUntilDate = (
  date: Date | string | number | null | undefined,
): number => {
  const timestamp = normalizeToTimestamp(date);
  if (!timestamp) return Infinity;
  return Math.ceil((timestamp - Date.now()) / MS_PER_DAY);
};

/**
 * Formats a timestamp for a chat message hover label, e.g. "July 15, 8:34 PM",
 * using the viewer's locale and local time zone. Date and time parts are formatted
 * separately and joined with a comma (Intl's combined format inserts "at").
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Localized "Month D, h:mm AM/PM" string, or '' if the timestamp is invalid
 */
export const formatMessageTime = (timestamp: number): string => {
  if (!Number.isFinite(timestamp)) return '';
  const date = new Date(timestamp);
  const datePart = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    day: 'numeric',
  }).format(date);
  const timePart = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  return `${datePart}, ${timePart}`;
};
